import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Loader2, Mail, Send, Eye } from "lucide-react";

// All fields available across all booking emails
const ALL_BOOKING_FIELDS = [
  { key: "booking_datetime", label: "Date & Time" },
  { key: "duration", label: "Duration" },
  { key: "requester_name", label: "Requester Name" },
  { key: "requester_email", label: "Requester Email", gdpr: true },
  { key: "requester_phone", label: "Requester Phone", gdpr: true },
  { key: "volunteer_name", label: "Volunteer Name" },
  { key: "volunteer_phone", label: "Volunteer Phone", gdpr: true },
  { key: "volunteer_email", label: "Volunteer Email", gdpr: true },
  { key: "meeting_point", label: "Meeting Point" },
  { key: "time_flexibility", label: "Time Flexibility" },
  { key: "extra_info", label: "Extra Information" },
  { key: "bike_choice", label: "Bike Choice" },
  { key: "booking_code", label: "Group Code" },
  { key: "status", label: "Booking Status" },
  { key: "trishaw_volunteer_name", label: "Trishaw Volunteer" },
  { key: "trishaw_volunteer_phone", label: "Trishaw Volunteer Phone", gdpr: true },
  { key: "wheelchair_volunteer_name", label: "Wheelchair Volunteer" },
  { key: "wheelchair_volunteer_phone", label: "Wheelchair Volunteer Phone", gdpr: true },
  { key: "trishaw_consent", label: "Trishaw Consent (Yes/No)" },
  { key: "request_consent", label: "Contact Consent (Yes/No)" },
  { key: "created_at", label: "Booking Submitted On" },
];

// Reminder emails also have hours_until
const REMINDER_FIELDS = [
  ...ALL_BOOKING_FIELDS,
  { key: "hours_until", label: "Hours Until Booking" },
];

const EMAIL_TYPES = [
  {
    key: "booking_received",
    label: "Booking Received",
    description: "Sent to the requester immediately after they submit the booking form.",
    variables: ["{requester_name}", "{booking_datetime}", "{duration}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "volunteer_request",
    label: "Volunteer Notification",
    description: "Sent to each volunteer when a new booking request is created.",
    variables: ["{requester_name}", "{requester_email}", "{requester_phone}", "{booking_datetime}", "{duration}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "volunteer_resend",
    label: "Volunteer Re-Notification",
    description: "Sent when an admin re-sends the volunteer notification for an unclaimed booking.",
    variables: ["{requester_name}", "{requester_email}", "{requester_phone}", "{booking_datetime}", "{duration}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "confirmation",
    label: "Booking Confirmation",
    description: "Sent to the requester once a volunteer has claimed and confirmed the booking.",
    variables: ["{requester_name}", "{booking_datetime}", "{duration}", "{volunteer_name}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "reminder_requester",
    label: "Reminder (Requester)",
    description: "Sent to the requester 24 hours and 2 hours before the booking.",
    variables: ["{requester_name}", "{volunteer_name}", "{booking_datetime}", "{duration}", "{hours_until}"],
    availableFields: REMINDER_FIELDS,
  },
  {
    key: "reminder_volunteer",
    label: "Reminder (Volunteer)",
    description: "Sent to the assigned volunteer 24 hours and 2 hours before the booking.",
    variables: ["{volunteer_name}", "{requester_name}", "{booking_datetime}", "{duration}", "{hours_until}"],
    availableFields: REMINDER_FIELDS,
  },
  {
    key: "volunteer_assignment",
    label: "Volunteer Assignment",
    description: "Sent to the volunteer when an admin manually confirms a booking and assigns them via the admin panel.",
    variables: ["{volunteer_name}", "{requester_name}", "{booking_datetime}", "{duration}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "volunteer_reassignment",
    label: "Volunteer Re-assignment",
    description: "Sent to the OLD volunteer when an admin swaps the assigned volunteer on an already-confirmed booking.",
    variables: ["{volunteer_name}", "{booking_datetime}", "{duration}"],
    availableFields: ALL_BOOKING_FIELDS,
  },
  {
    key: "meeting_point_update",
    label: "Meeting Point Update",
    description: "Sent to the requester when an admin changes the meeting point on an existing booking.",
    variables: ["{requester_name}", "{booking_datetime}"],
    availableFields: [],
  },
];

const DEFAULTS = {
  booking_received: {
    subject: "Booking Request Received - Dodder Valley Cycling",
    intro: "Thank you for your booking request! We have received it and our volunteers have been notified. You will receive a confirmation email once a volunteer has confirmed your booking.",
    outro: "We will be in touch shortly once a volunteer confirms your booking.",
    fields: ["booking_datetime", "duration", "meeting_point", "time_flexibility"],
  },
  volunteer_request: {
    subject: "New Bike Booking Request - {booking_datetime}",
    intro: "A new accessible bike booking request has been made. If you are available to assist, please claim this booking.",
    outro: "First volunteer to claim wins. Once claimed, this link becomes inactive.",
    fields: ["requester_name", "requester_email", "requester_phone", "booking_datetime", "duration", "meeting_point", "time_flexibility"],
  },
  volunteer_resend: {
    subject: "Booking Request Still Not Claimed - {booking_datetime}",
    intro: "Reminder: The following booking request has not yet been claimed by a volunteer. If you are available to assist, please claim it as soon as possible.",
    outro: "First volunteer to claim wins. Once claimed, this link becomes inactive.",
    fields: ["requester_name", "requester_email", "requester_phone", "booking_datetime", "duration", "meeting_point", "time_flexibility"],
  },
  confirmation: {
    subject: "Your Accessible Bike Booking is Confirmed!",
    intro: "Great news! Your accessible bike booking has been confirmed. A volunteer will be there to assist you.",
    outro: "You will receive reminder emails 24 hours and 2 hours before your booking.",
    fields: ["booking_datetime", "duration", "volunteer_name", "volunteer_phone", "meeting_point"],
  },
  reminder: {
    subject: "Reminder: Accessible Bike Booking in {hours_until} Hour(s)",
    intro: "This is a friendly reminder about an upcoming accessible bike booking in {hours_until} hour(s).",
    outro: "Please ensure you are prepared for this booking.",
    fields: ["booking_datetime", "duration", "requester_name", "volunteer_name", "volunteer_phone", "meeting_point"],
  },
  reminder_requester: {
    subject: "Reminder: Your Accessible Bike Booking in {hours_until} Hour(s)",
    intro: "This is a friendly reminder about your upcoming accessible bike booking in {hours_until} hour(s).",
    outro: "Please ensure you are at the meeting point on time. If you need to cancel, please let us know as soon as possible.",
    fields: ["booking_datetime", "duration", "volunteer_name", "volunteer_phone", "meeting_point"],
  },
  reminder_volunteer: {
    subject: "Reminder: Volunteer Booking in {hours_until} Hour(s)",
    intro: "This is a friendly reminder about an upcoming accessible bike booking you are volunteering for in {hours_until} hour(s).",
    outro: "Please ensure you are at the meeting point on time. If you cannot make it, please contact the admin immediately.",
    fields: ["booking_datetime", "duration", "requester_name", "requester_phone", "meeting_point", "time_flexibility"],
  },
  volunteer_assignment: {
    subject: "You've Been Assigned to a Booking - {booking_datetime}",
    intro: "You have been assigned to assist with an accessible bike booking by an administrator. Please note the booking details below.",
    outro: "Please contact the club admin if you have any questions or are unable to attend.",
    fields: ["booking_datetime", "duration", "requester_name", "requester_email", "requester_phone", "meeting_point", "time_flexibility"],
  },
  volunteer_reassignment: {
    subject: "Booking Re-assigned - {booking_datetime}",
    intro: "Your assignment to assist with an accessible bike booking has been updated by an administrator. Another volunteer has been assigned in your place.",
    outro: "If you believe this is an error, please contact the club admin.",
    fields: ["booking_datetime", "duration"],
  },
  meeting_point_update: {
    subject: "Meeting Point Updated - {booking_datetime}",
    intro: "An administrator has updated the meeting point for your accessible bike booking. Please note the new meeting location.",
    outro: "If you have any questions about this change, please contact us.",
    fields: [],
  },
};

export default function AdminEmailAdmin() {
  const [templates, setTemplates] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testModal, setTestModal] = useState(null); // { emailType, label } | null
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [previewModal, setPreviewModal] = useState(null); // { label, html } | null
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.getSettings()
      .then((r) => {
        const stored = r.data.email_templates || {};
        setTemplates((prev) => {
          const merged = { ...prev };
          for (const key of Object.keys(prev)) {
            if (stored[key]) merged[key] = { ...prev[key], ...stored[key] };
          }
          return merged;
        });
        // Pre-fill test email with admin's email if available
        const adminEmail = localStorage.getItem("admin_email") || "";
        if (adminEmail) setTestEmail(adminEmail);
      })
      .catch(() => toast.error("Failed to load email templates"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, field, value) => {
    setTemplates((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const handleFieldToggle = (key, fieldKey) => {
    setTemplates((prev) => {
      const current = prev[key].fields || [];
      const updated = current.includes(fieldKey)
        ? current.filter((f) => f !== fieldKey)
        : [...current, fieldKey];
      return { ...prev, [key]: { ...prev[key], fields: updated } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ email_templates: templates });
      toast.success("Email templates saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save templates");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (key) => {
    setTemplates((prev) => ({ ...prev, [key]: DEFAULTS[key] }));
    toast.info("Reset to default — click Save to apply.");
  };

  const openTestModal = (type) => setTestModal(type);
  const closeTestModal = () => { setTestModal(null); setTesting(false); };

  const handlePreview = async (type) => {
    setPreviewing(true);
    const tmpl = templates[type.key] || {};
    try {
      const res = await api.previewEmail(type.key, tmpl.intro || null, tmpl.outro || null, tmpl.fields || null);
      setPreviewModal({ label: type.label, html: res.data.html });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) { toast.error("Please enter a test email address"); return; }
    setTesting(true);
    try {
      const res = await api.sendTestEmail(testModal.key, testEmail);
      toast.success(res.data.message);
      closeTestModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin text-green-700" />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Admin</h1>
          <p className="text-slate-500 mt-1">Customise the subject, opening, and closing text for each system email</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-email-templates-btn"
          className="gap-2 bg-[#15803d] hover:bg-green-800 text-white rounded-full px-6"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save All Templates"}
        </Button>
      </div>

      <Tabs defaultValue="booking_received">
        <TabsList className="mb-6 bg-white border border-green-100 shadow-sm rounded-xl p-1 h-auto flex-wrap gap-1 w-full justify-start">
          {EMAIL_TYPES.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`email-tab-${t.key}`}
              className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-green-700 data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {EMAIL_TYPES.map((type) => (
          <TabsContent key={type.key} value={type.key}>
            <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-green-700" />
                  <div>
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{type.label}</h2>
                    <p className="text-slate-500 text-sm mt-0.5">{type.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handlePreview(type)}
                    disabled={previewing}
                    className="text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                    data-testid={`preview-${type.key}`}
                  >
                    {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                    Preview
                  </button>
                  <span className="text-slate-200">|</span>
                  <button
                    onClick={() => openTestModal(type)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                    data-testid={`send-test-${type.key}`}
                  >
                    <Send className="w-3.5 h-3.5" /> Send Test
                  </button>
                  <span className="text-slate-200">|</span>
                  <button
                    onClick={() => handleReset(type.key)}
                    className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                    data-testid={`reset-${type.key}-btn`}
                  >
                    Reset to default
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Subject */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 block">Subject Line</Label>
                  <Input
                    data-testid={`subject-${type.key}`}
                    value={templates[type.key]?.subject || ""}
                    onChange={(e) => handleChange(type.key, "subject", e.target.value)}
                    className="h-11 bg-slate-50 border-slate-200 font-medium"
                    placeholder="Email subject..."
                  />
                </div>

                {/* Intro */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 block">Opening Text</Label>
                  <p className="text-xs text-slate-400 mb-2">Appears as the first paragraph of the email body, after the greeting.</p>
                  <textarea
                    data-testid={`intro-${type.key}`}
                    value={templates[type.key]?.intro || ""}
                    onChange={(e) => handleChange(type.key, "intro", e.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-green-600 focus:bg-white resize-none transition-colors"
                    placeholder="Opening paragraph..."
                  />
                </div>

                {/* Outro */}
                <div>
                  <Label className="text-slate-700 font-semibold mb-2 block">Closing Text</Label>
                  <p className="text-xs text-slate-400 mb-2">Appears after the booking details table, before the footer.</p>
                  <textarea
                    data-testid={`outro-${type.key}`}
                    value={templates[type.key]?.outro || ""}
                    onChange={(e) => handleChange(type.key, "outro", e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-green-600 focus:bg-white resize-none transition-colors"
                    placeholder="Closing paragraph..."
                  />
                </div>

                {/* Table Fields */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Booking Details Table Fields</p>
                  <p className="text-xs text-slate-400 mb-3">Choose which rows appear in the booking details table within this email. Uncheck personal data fields to comply with GDPR.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {type.availableFields.map((field) => {
                      const checked = (templates[type.key]?.fields || []).includes(field.key);
                      return (
                        <label key={field.key} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleFieldToggle(type.key, field.key)}
                            data-testid={`field-${type.key}-${field.key}`}
                            className="w-4 h-4 accent-green-700 shrink-0"
                          />
                          <span className="text-sm text-slate-700 font-medium">{field.label}</span>
                          {field.gdpr && (
                            <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium shrink-0">Personal data</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Variables reference */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Available Variables</p>
                  <div className="flex flex-wrap gap-2">
                    {type.variables.map((v) => (
                      <span
                        key={v}
                        className="inline-flex items-center px-3 py-1 bg-white border border-green-200 text-green-800 rounded-full text-xs font-mono font-medium shadow-sm select-all cursor-pointer"
                        title="Click to copy"
                        onClick={() => { navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); }}
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">These placeholders are replaced with live data when the email is sent. Click to copy.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Send Test Email Dialog */}
      <Dialog open={!!testModal} onOpenChange={(open) => { if (!open) closeTestModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Send Test — {testModal?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-slate-500">
              A test version of this email will be sent using your current (unsaved) template content and sample booking data.
            </p>
            <div>
              <Label className="font-semibold text-slate-700 mb-2 block">Test email address</Label>
              <Input
                data-testid="test-email-input"
                type="email"
                placeholder="e.g. you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendTest()}
                className="h-11"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeTestModal} disabled={testing}>Cancel</Button>
            <Button
              data-testid="confirm-send-test-btn"
              onClick={handleSendTest}
              disabled={testing || !testEmail}
              className="gap-2 bg-[#15803d] hover:bg-green-800 text-white"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {testing ? "Sending..." : "Send Test Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={!!previewModal} onOpenChange={(open) => { if (!open) setPreviewModal(null); }}>
        <DialogContent className="max-w-2xl w-full p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Eye className="w-4 h-4 text-green-700" />
              Preview — {previewModal?.label}
            </DialogTitle>
            <p className="text-xs text-slate-400 mt-0.5">Sample data is shown. Fields reflect your current (unsaved) template settings.</p>
          </DialogHeader>
          <div className="h-[560px] overflow-hidden">
            {previewModal?.html && (
              <iframe
                data-testid="email-preview-iframe"
                srcDoc={previewModal.html}
                title="Email Preview"
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
              />
            )}
          </div>
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex justify-end">
            <Button variant="outline" onClick={() => setPreviewModal(null)} className="text-sm">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
